import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Square, Briefcase, Loader2, Clock, ArrowRightLeft, X, Fuel } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { api, formatApiError } from "../../lib/api";
import {
    enqueueCheckIn, enqueueCheckOut, broadcastPending,
} from "../../lib/offlineQueue";
import dict from "../../i18n/es-MX";

function greetingFor(date) {
    const h = date.getHours();
    if (h < 12) return dict.operator.greetingMorning;
    if (h < 19) return dict.operator.greetingAfternoon;
    return dict.operator.greetingEvening;
}

const fmtTime = (iso) => iso
    ? new Date(iso).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })
    : "";

// Small confirm modal for start / end shift
function ShiftModal({ open, mode, jobs, onCancel, onConfirm, busy }) {
    const [jobId, setJobId] = useState("");
    const [note, setNote] = useState("");
    useEffect(() => {
        if (open) { setJobId(""); setNote(""); }
    }, [open]);
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60" data-testid="shift-modal">
            <div className="w-full sm:max-w-md bg-slate-900 rounded-t-2xl sm:rounded-2xl border border-slate-700 p-5 space-y-4">
                <div className="flex items-baseline justify-between">
                    <h2 className="font-display text-lg font-bold">
                        {mode === "start" ? dict.attendance.startModalTitle : dict.attendance.endModalTitle}
                    </h2>
                    <button type="button" onClick={onCancel} data-testid="shift-modal-close" className="text-slate-400 hover:text-slate-200"><X size={18}/></button>
                </div>
                {mode === "start" && (
                    <div>
                        <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">{dict.attendance.pickJob}</label>
                        <select value={jobId} onChange={e => setJobId(e.target.value)} data-testid="shift-modal-job-select"
                            className="w-full h-11 rounded-lg bg-slate-950 border border-slate-700 text-slate-100 px-3">
                            <option value="">{dict.attendance.pickJobGeneral}</option>
                            {jobs.map(j => (
                                <option key={j.id} value={j.id}>{j.code} · {j.client_name || "—"}</option>
                            ))}
                        </select>
                    </div>
                )}
                <div>
                    <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">{dict.attendance.noteLabel}</label>
                    <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} data-testid="shift-modal-note"
                        placeholder={dict.attendance.notePlaceholder}
                        className="w-full rounded-lg bg-slate-950 border border-slate-700 text-slate-100 px-3 py-2"/>
                </div>
                <div className="flex gap-2">
                    <button type="button" onClick={onCancel} data-testid="shift-modal-cancel"
                        className="flex-1 h-11 rounded-lg border border-slate-700 bg-slate-950 text-slate-300 text-sm">
                        {dict.attendance.cancel}
                    </button>
                    <button type="button" onClick={() => onConfirm({ job_id: jobId || null, note: note.trim() || null })}
                        disabled={busy} data-testid="shift-modal-submit"
                        className={`flex-1 h-11 rounded-lg font-semibold text-sm inline-flex items-center justify-center gap-2 ${mode==="start"?"bg-brand-orange text-slate-950":"bg-slate-800 border-2 border-brand-orange text-brand-orange"} disabled:opacity-60`}>
                        {busy && <Loader2 className="animate-spin" size={14}/>}
                        {mode === "start" ? dict.attendance.startBtn : dict.attendance.endBtn}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function OperatorHome() {
    const { user } = useAuth();
    const [today, setToday] = useState({ sessions: [], open_session: null });
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [toast, setToast] = useState("");
    const [modal, setModal] = useState(null); // "start" | "end" | null
    const [fuelModal, setFuelModal] = useState(false);
    const [assetsList, setAssetsList] = useState([]);
    const greeting = greetingFor(new Date());

    const load = useCallback(async () => {
        setLoading(true);
        setErr("");
        // Use allSettled so a 403 on any single endpoint (e.g. /assets restricted
        // for some ops) does not blank out the whole dashboard.
        const results = await Promise.allSettled([
            api.get("/attendance/today"),
            api.get("/jobs/my", { params: { status: "en_progreso" } }),
            api.get("/assets"),
        ]);
        const [aRes, jRes, asRes] = results;
        if (aRes.status === "fulfilled") setToday(aRes.value.data || { sessions: [], open_session: null });
        else setToday({ sessions: [], open_session: null });
        if (jRes.status === "fulfilled") setJobs(jRes.value.data || []);
        else setJobs([]);
        if (asRes.status === "fulfilled") setAssetsList((asRes.value.data || []).filter(x => !x.archived));
        else setAssetsList([]);
        // Only surface an error if EVERYTHING failed (network down / auth gone).
        if (results.every(r => r.status === "rejected")) {
            setErr(formatApiError(results[0].reason));
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2500); };

    const doCheckIn = async (payload) => {
        setBusy(true); setErr("");
        try {
            await api.post("/attendance/check-in", payload);
            setModal(null);
            await load();
        } catch (e) {
            if (!e?.response) {
                await enqueueCheckIn({ body: payload });
                await broadcastPending();
                showToast(dict.attendance.queuedCheckIn);
                setModal(null);
            } else {
                setErr(formatApiError(e) || dict.attendance.checkInError);
            }
        } finally { setBusy(false); }
    };

    const doCheckOut = async (payload) => {
        setBusy(true); setErr("");
        try {
            await api.post("/attendance/check-out", payload);
            setModal(null);
            await load();
        } catch (e) {
            if (!e?.response) {
                await enqueueCheckOut({ body: payload });
                await broadcastPending();
                showToast(dict.attendance.queuedCheckOut);
                setModal(null);
            } else {
                setErr(formatApiError(e) || dict.attendance.checkOutError);
            }
        } finally { setBusy(false); }
    };

    const changeJob = async () => {
        if (!window.confirm(dict.attendance.changeJobConfirm)) return;
        // Close current, then open modal for new one
        await doCheckOut({});
        setModal("start");
    };

    const open = today.open_session;
    const inShift = !!open;

    const ongoingLabel = useMemo(() => {
        if (!open) return "";
        return dict.attendance.ongoingBanner.replace("{time}", fmtTime(open.session_start_at));
    }, [open]);

    return (
        <div data-testid="operator-home" className="px-4 py-5 space-y-5">
            <div>
                <p className="text-sm text-slate-400">{greeting}</p>
                <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-50">
                    {user?.full_name || user?.name || ""}
                </h1>
            </div>

            {loading ? (
                <div className="py-10 text-center"><Loader2 className="animate-spin inline text-slate-500"/></div>
            ) : (
                <>
                    {!inShift && (
                        <button
                            type="button" onClick={() => setModal("start")} disabled={busy}
                            data-testid="shift-toggle-button"
                            className="w-full min-h-[72px] rounded-2xl font-semibold uppercase tracking-wider text-base flex items-center justify-center gap-3 transition shadow-[0_4px_0_rgba(0,0,0,0.4)] active:translate-y-[2px] active:shadow-[0_2px_0_rgba(0,0,0,0.4)] bg-brand-orange hover:bg-orange-400 active:bg-orange-600 text-slate-950 disabled:opacity-60"
                        >
                            {busy ? <Loader2 className="animate-spin" size={22}/> : <Play size={26}/>}
                            {dict.attendance.startShift}
                        </button>
                    )}

                    {inShift && (
                        <>
                            <button
                                type="button" onClick={() => setModal("end")} disabled={busy}
                                data-testid="shift-toggle-button"
                                className="w-full min-h-[72px] rounded-2xl font-semibold uppercase tracking-wider text-base flex items-center justify-center gap-3 transition shadow-[0_4px_0_rgba(0,0,0,0.4)] active:translate-y-[2px] active:shadow-[0_2px_0_rgba(0,0,0,0.4)] bg-slate-800 border-2 border-brand-orange text-brand-orange disabled:opacity-60"
                            >
                                {busy ? <Loader2 className="animate-spin" size={22}/> : <Square size={26}/>}
                                {dict.attendance.endShift}
                            </button>
                            <div data-testid="shift-active-row"
                                className="rounded-xl border border-brand-orange/40 bg-brand-orange/5 px-4 py-3 text-sm space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-brand-orange font-medium inline-flex items-center gap-1">
                                        <Clock size={14}/>{ongoingLabel}
                                    </span>
                                    <button type="button" onClick={changeJob} disabled={busy} data-testid="shift-change-job"
                                        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-brand-orange hover:underline">
                                        <ArrowRightLeft size={11}/> {dict.hours.changeJobBtn}
                                    </button>
                                </div>
                                <div className="text-xs text-slate-300" data-testid="shift-active-job">
                                    {open?.job_id
                                        ? dict.attendance.jobInProgress.replace("{job}", open.job_code || "").replace("{client}", open.client_name || "—")
                                        : dict.attendance.workingGeneral}
                                </div>
                            </div>
                        </>
                    )}

                    {err && (
                        <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200" data-testid="home-err">
                            {err}
                        </div>
                    )}

                    <Link
                        to="/campo/trabajos" data-testid="jobs-today-card"
                        className="block rounded-2xl border border-slate-800 bg-slate-900/60 hover:border-brand-orange transition p-5 flex items-center gap-4"
                    >
                        <div className="w-12 h-12 rounded-xl bg-brand-orange/15 flex items-center justify-center">
                            <Briefcase size={22} className="text-brand-orange" />
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-wider text-slate-400">
                                {dict.operator.myJobsToday}
                            </div>
                            <div className="font-display text-2xl font-bold mt-0.5" data-testid="jobs-today-count">
                                {jobs.length === 0
                                    ? dict.operator.zeroJobs
                                    : `${jobs.length} ${jobs.length === 1 ? dict.operator.singularJob : dict.operator.pluralJobs}`}
                            </div>
                        </div>
                    </Link>

                    <button
                        type="button" onClick={() => setFuelModal(true)} data-testid="operator-fuel-btn"
                        className="w-full rounded-2xl border border-slate-800 bg-slate-900/60 hover:border-brand-orange transition p-4 flex items-center gap-3 text-left"
                    >
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center shrink-0">
                            <Fuel size={20} className="text-emerald-400"/>
                        </div>
                        <div className="min-w-0">
                            <div className="text-sm font-semibold text-slate-100">{dict.fuel.operatorHome.chargeBtn}</div>
                            <div className="text-[11px] text-slate-500">{dict.fuel.subtitle}</div>
                        </div>
                    </button>

                    <ShiftModal
                        open={modal === "start"} mode="start" jobs={jobs} busy={busy}
                        onCancel={() => setModal(null)} onConfirm={doCheckIn}
                    />
                    <ShiftModal
                        open={modal === "end"} mode="end" jobs={jobs} busy={busy}
                        onCancel={() => setModal(null)} onConfirm={doCheckOut}
                    />
                    <OperatorFuelModal
                        open={fuelModal}
                        assets={assetsList}
                        onClose={() => setFuelModal(false)}
                        onSaved={(msg) => { setFuelModal(false); showToast(msg); }}
                    />

                    {toast && (
                        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 px-4 py-2 rounded-md text-sm shadow-lg z-50 bg-slate-800 text-slate-100 border border-slate-700" data-testid="home-toast">
                            {toast}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// Simple fuel modal for operators — carga general sin job
function OperatorFuelModal({ open, assets, onClose, onSaved }) {
    const [assetId, setAssetId] = useState("");
    const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [fuelType, setFuelType] = useState("diesel");
    const [liters, setLiters] = useState("");
    const [cost, setCost] = useState("");
    const [method, setMethod] = useState("efectivo");
    const [voucher, setVoucher] = useState("");
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (open) {
            setAssetId(""); setDate(new Date().toISOString().slice(0, 10));
            setFuelType("diesel"); setLiters(""); setCost("");
            setMethod("efectivo"); setVoucher(""); setFile(null); setErr("");
        }
    }, [open]);

    if (!open) return null;
    const price = (parseFloat(liters) > 0 && parseFloat(cost) > 0)
        ? (parseFloat(cost) / parseFloat(liters)).toFixed(2) : null;

    const submit = async (e) => {
        e.preventDefault();
        if (!assetId) { setErr("Selecciona un activo"); return; }
        if (parseFloat(liters) <= 0) { setErr("Litros debe ser mayor a 0"); return; }
        if (parseFloat(cost) <= 0) { setErr("Costo debe ser mayor a 0"); return; }
        setBusy(true); setErr("");
        try {
            const fd = new FormData();
            fd.append("asset_id", assetId);
            fd.append("date", date);
            fd.append("fuel_type", fuelType);
            fd.append("liters", liters);
            fd.append("total_cost_mxn", cost);
            fd.append("payment_method", method);
            if (voucher) fd.append("voucher_number", voucher);
            if (file) fd.append("file", file);
            await api.post("/fuel-logs", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onSaved(dict.fuel.saveOk);
        } catch (e) { setErr(formatApiError(e) || "Error al guardar"); }
        finally { setBusy(false); }
    };

    const FUEL_TYPES = ["gasolina", "diesel"];
    const OP_METHODS = ["efectivo", "vale", "tarjeta_combustible"];

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60" data-testid="operator-fuel-modal">
            <form onSubmit={submit} noValidate className="w-full sm:max-w-md bg-slate-900 rounded-t-2xl sm:rounded-2xl border border-slate-700 p-5 space-y-3 max-h-[95vh] overflow-y-auto">
                <div className="flex items-baseline justify-between">
                    <h2 className="font-display text-lg font-bold">{dict.fuel.operatorHome.modalTitle}</h2>
                    <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-200" data-testid="operator-fuel-close"><X size={18}/></button>
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">{dict.fuel.form.asset}</label>
                    <select value={assetId} onChange={e => setAssetId(e.target.value)} data-testid="operator-fuel-asset"
                        className="w-full h-11 rounded-lg bg-slate-950 border border-slate-700 text-slate-100 px-3">
                        <option value="">{dict.fuel.form.pickAsset}</option>
                        {assets.map(a => <option key={a.id} value={a.id}>{a.nickname} · {a.subtype}</option>)}
                    </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">{dict.fuel.form.date}</label>
                        <input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="operator-fuel-date"
                            className="w-full h-11 rounded-lg bg-slate-950 border border-slate-700 text-slate-100 px-3"/>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">{dict.fuel.form.fuelType}</label>
                        <div className="flex gap-1">
                            {FUEL_TYPES.map(t => (
                                <button type="button" key={t} onClick={() => setFuelType(t)} data-testid={`operator-fuel-type-${t}`}
                                    className={`flex-1 h-11 rounded-lg text-xs uppercase border ${fuelType===t?"bg-brand-orange text-slate-950 border-brand-orange font-semibold":"bg-slate-950 text-slate-300 border-slate-700"}`}>
                                    {dict.fuel.types[t]}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                    <div>
                        <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">{dict.fuel.form.liters}</label>
                        <input type="number" step="0.01" inputMode="decimal" value={liters} onChange={e => setLiters(e.target.value)} data-testid="operator-fuel-liters"
                            className="w-full h-11 rounded-lg bg-slate-950 border border-slate-700 text-slate-100 px-3 font-mono"/>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">{dict.fuel.form.totalCost}</label>
                        <input type="number" step="0.01" inputMode="decimal" value={cost} onChange={e => setCost(e.target.value)} data-testid="operator-fuel-cost"
                            className="w-full h-11 rounded-lg bg-slate-950 border border-slate-700 text-slate-100 px-3 font-mono"/>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">$/L</label>
                        <div className="h-11 flex items-center justify-center font-mono text-brand-orange font-bold" data-testid="operator-fuel-price">
                            {price != null ? `$${price}` : "—"}
                        </div>
                    </div>
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">{dict.fuel.form.paymentMethod}</label>
                    <div className="flex gap-1 flex-wrap">
                        {OP_METHODS.map(m => (
                            <button type="button" key={m} onClick={() => setMethod(m)} data-testid={`operator-fuel-method-${m}`}
                                className={`flex-1 h-10 rounded-lg text-xs uppercase border ${method===m?"bg-brand-orange text-slate-950 border-brand-orange font-semibold":"bg-slate-950 text-slate-300 border-slate-700"}`}>
                                {dict.fuel.methods[m]}
                            </button>
                        ))}
                    </div>
                </div>
                {(method === "vale" || method === "tarjeta_combustible") && (
                    <div>
                        <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">{dict.fuel.form.voucher}</label>
                        <input type="text" value={voucher} onChange={e => setVoucher(e.target.value)} data-testid="operator-fuel-voucher"
                            className="w-full h-11 rounded-lg bg-slate-950 border border-slate-700 text-slate-100 px-3" placeholder="V-1234"/>
                    </div>
                )}
                <div>
                    <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">{dict.fuel.form.receipt}</label>
                    <input type="file" accept="image/*" capture="environment" onChange={e => setFile(e.target.files?.[0] || null)}
                        data-testid="operator-fuel-file" className="text-xs text-slate-300"/>
                </div>
                {err && <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-2 text-xs text-red-200" data-testid="operator-fuel-error">{err}</div>}
                <div className="flex gap-2">
                    <button type="button" onClick={onClose} className="flex-1 h-11 rounded-lg border border-slate-700 bg-slate-950 text-slate-300 text-sm" data-testid="operator-fuel-cancel">
                        {dict.fuel.form.cancel}
                    </button>
                    <button type="submit" disabled={busy} className="flex-1 h-11 rounded-lg bg-brand-orange text-slate-950 font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="operator-fuel-submit">
                        {busy && <Loader2 className="animate-spin" size={14}/>}
                        Registrar
                    </button>
                </div>
            </form>
        </div>
    );
}

